<template>
  <div>
    <HeaderBreadcrumbs
      :items="[{ label: 'Admin' }]"
      title="All Admin"
    />

    <!-- Table actions: Search and filter -->
    <div class="flex-col w-full bg-white rounded-lg">
      <div class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg ">

        <div class="relative w-2/6">
          <SearchOutline className="text-xl absolute top-1/2 left-3 transform -translate-y-1/2" />
          <input
            type="search"
            placeholder="Search Email, Publisher"
            class="p-2.5 pl-5 w-full text-sm border rounded-md bg-gray-50"
            v-model="searchTerm"
            @keyup.enter="onSearch"
            @input="onSearch"
          />
        </div>

        <div class="relative flex justify-end w-3/4">
          <Button
            @click="$router.push('/user-admin/create')"
            variant="default"
            color="primary"
            class="mr-3"
          >
            <template #iconBefore>
              <i class="fa fa-plus"></i>
            </template>
            Add New
          </Button>

          <!-- Filter button with dropdown -->
          <div class="relative" ref="filterContainer">
            <Button
              @click="showFilterModal = !showFilterModal"
              variant="outlined"
              color="alternative"
              class="relative"
            >
              <template #iconBefore>
                <FilterFilled className="h-3 w-3 text-gray-800" />
              </template>
              Filter
              <template #iconAfter>
                <ChevronDownFilled className="h-3 w-3 text-gray-800" />
              </template>

              <!-- Filter count badge -->
              <span
                v-if="activeFilterCount > 0"
                class="absolute flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full -top-2 -right-2"
              >
                {{ activeFilterCount }}
              </span>
            </Button>

            <!-- Filter Dropdown -->
            <AdminFilterModal
              :visible="showFilterModal"
              :initial-filters="appliedFilters"
              @apply-filter="handleApplyFilter"
              @reset-filter="handleResetFilter"
              @update:visible="showFilterModal = false"
            />
          </div>
        </div>

      </div>
    </div>

    <Table
      :headers="tableHeaders"
      :data="admins"
      :rowKey="tableRowKeys"
      :currentPage="currentPage"
      :perPage="perPage"
      :isLoading="isLoading"
      className="mb-2"
      @sort="handleTableSort"
    >
      <template #email="{ value }">
        <div class="lowercase">
          {{ value || '-' }}
        </div>
      </template>
      <template #group="{ value }">
        <div>

          <Label
            v-if="value === 'ebook'"
            variant="primary"
          >
            Ebook
          </Label>

          <Label
            v-if="value === 'publisher'"
            variant="yellow"
          >
            Publisher
          </Label>
  <Label
            v-if="value === 'publisher_group'"
            variant="orange"
          >
            Publisher Group
          </Label>
        </div>
      </template>
      <template #status="{ row }">
        <div>
          <Label
            :variant="row?.is_active ? 'green' : 'gray'"
          >
            {{ row?.is_active ? 'Active' : 'Inactive' }}
          </Label>
        </div>
      </template>
      <template #createdAt='{ value }'>
        <div>
          {{ formatDate(value) }}
        </div>
        <div class="text-xs italic text-gray-500">
          {{ formatTime(value) }}
        </div>
      </template>
      <template #updatedAt='{ value }'>
        <div>
          {{ formatDate(value) }}
        </div>
        <div class="text-xs italic text-gray-500">
          {{ formatTime(value) }}
        </div>
      </template>
      <template #actions="{ row }">
        <t-dropdown class="relative">
          <div    :class="{
              'cursor-not-allowed opacity-50 pointer-events-none': userData.role_id === 14 && row.role_id === 1
            }" slot="trigger" slot-scope="{
            mousedownHandler,
            focusHandler,
            blurHandler,
            keydownHandler,
            isShown
          }">
            <button id="user-menu" :class="{ ' text-gray-500 ': isShown }" aria-label="User menu"
              aria-haspopup="true" @focus.stop="focusHandler" @blur.stop="blurHandler"
              @keydown.stop="keydownHandler" @click.stop="mousedownHandler"
              class="cursor-pointer focus:outline-none">
              <i class="fa-solid fa-ellipsis"></i>
            </button>
          </div>

          <div 
           class="z-50 flex flex-col flex-grow-0 p-1">
            <router-link
              class="flex items-center px-3 py-2 text-sm font-normal text-gray-700 no-underline transition-colors duration-150 rounded-lg hover:bg-gray-50"
              :to="`/user-admin/edit/${row.id}`"
            >
              <EditFilled class="w-4 h-4 mr-2" />
              Edit Admin
            </router-link>
            <router-link
              class="flex items-center px-3 py-2 text-sm font-normal text-gray-700 no-underline transition-colors duration-150 rounded-lg hover:bg-gray-50"
              :to="`/user-admin/display/${row.id}`"
            >
              <EyeFilled class="w-4 h-4 mr-2" />
              View Admin
            </router-link>
          </div>
        </t-dropdown>
      </template>
    </Table>

    <!-- Pagination -->
    <div class="flex justify-between pt-4 pl-3 pr-3 mt-1 mb-2 bg-white border-t border-gray-100" v-if="Number(totalRows)">
      <div class="font-semibold">
        <span class="font-normal text-gray-500">Showing </span> {{ currentPage }}-{{ Number(totalRows) >= perPage ? perPage : Number(totalRows) }} <span
          class="font-normal text-gray-500">of</span> {{ parseInt(totalRows) }}
      </div>
      <div class="mb-2">
        <t-pagination :disabled="isLoading" :total-items="Number(totalRows)" :per-page="perPage" v-model="currentPage"
          @change="onClickPage" />
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment';
import { mapState, mapActions } from 'vuex';

import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import SearchOutline from '@/components/icons/SearchOutline';
import FilterFilled from '@/components/icons/FilterFilled';
import ChevronDownFilled from '@/components/icons/ChevronDownFilled';
import Table from '@/components/form/table';
import Label from '@/components/ui/Label';
import Button from '@/components/ui/Button';

import EditFilled from '@/components/icons/EditFilled';
import EyeFilled from '@/components/icons/EyeFilled';
import AdminFilterModal from '@/components/ui/AdminFilterModal';

export default {
  name: 'UserAdminNew',
  components: {
    HeaderBreadcrumbs,
    SearchOutline,
    Table,
    Label,
    Button,
    EditFilled,
    EyeFilled,
    FilterFilled,
    ChevronDownFilled,
    AdminFilterModal
  },
  data() {
    return {
      searchTerm: null,
      perPage: 15,
      currentPage: 1,
      group: '',
      userData: JSON.parse(localStorage.getItem('session_auth')),
      // Filter modal state
      showFilterModal: false,
      appliedFilters: {
        vendors: [],
        role: null,
        group: null,
        status: null,
        search: null
      },


      // Table settings
      tableHeaders: [
        { label: 'No', key: 'no' },
        { label: 'Email', key: 'email' },
        { label: 'Publisher', key: 'vendor' },
        { label: 'Role', key: 'role' },
        { label: 'Group', key: 'group' },
        { label: 'Status', key: 'status' },
        { label: 'Created Date', key: 'created_date', sortable: true, style: 'min-width: 120px' },
        { label: 'Last Modified', key: 'last_modified', sortable: true, style: 'min-width: 120px' },

      ],

      tableRowKeys: [
        'email',
        'vendor_name',
        'role_name',
        'group',
        'status',
        'createdAt',
        'updatedAt'
      ],

      // Sorting
      activeSort: {
        key: null,
        order: 'asc'
      },

      // Debounce for search
      debounce: null
    }
  },
  computed: {
    ...mapState({
      admins: (state) => state.admin.items,
      isLoading: (state) => state.admin.isLoading,
      isError: (state) => state.admin.isError,
      totalRows: (state) => state.admin.totalRows,
    }),

    activeFilterCount() {
      let count = 0;
      if (this.appliedFilters.vendors && this.appliedFilters.vendors.length > 0) count++;
      if (this.appliedFilters.role) count++;
      if (this.appliedFilters.group) count++;
      if (this.appliedFilters.status) count++;
      return count;
    }
  },
  mounted() {
    this.actionFetchAll();
    // Add click outside listener for filter modal
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    currentPage: function() {
      this.actionFetchAll();
    },
  },
  methods: {
    ...mapActions('admin', ['fetchAdmin', 'search']),
    ...mapActions('vendors', ['findVendor']),
    actionFetchAll() {
      let payload = {
        page: this.currentPage,
        limit: this.perPage,
      };

      // Add search term
      if (this.appliedFilters.search) payload.search = this.appliedFilters.search;
      if (this.searchTerm) payload.search = this.searchTerm;

      // Add filter parameters
      if (this.appliedFilters.vendors && this.appliedFilters.vendors.length > 0) {
        payload.vendor = this.appliedFilters.vendors.map(v => v.id).join(',');
      }
      if (this.appliedFilters.role) {
        payload.role = this.appliedFilters.role;
      }
      if (this.appliedFilters.group) {
        payload.group = this.appliedFilters.group;
      }
      if (this.appliedFilters.status !== null && this.appliedFilters.status !== undefined) {
        payload.status = this.appliedFilters.status;
      }

      // Add sorting parameters
      if (this.activeSort.key) {
        payload.sort = this.activeSort.key;
        payload.order = this.activeSort.order;
      }

      this.fetchAdmin(payload);
    },
    onSearch(event) {
      const value = event.target.value;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.searchTerm = value;
        this.appliedFilters.search = value;
        this.currentPage = 1;
        this.actionFetchAll();
      }, 600);
    },

    handleApplyFilter(filterData) {
      this.appliedFilters = { ...this.appliedFilters, ...filterData };
      this.currentPage = 1; // Reset to first page when applying filters
      this.actionFetchAll();
    },

    handleResetFilter() {
      this.appliedFilters = {
        vendors: [],
        role: null,
        group: null,
        status: null,
        search: null
      };
      this.searchTerm = null;
      this.currentPage = 1; // Reset to first page when resetting filters
      this.actionFetchAll();
    },

    handleClickOutside(event) {
      // Check if the click is outside the filter container and not on any dropdown elements
      if (this.$refs.filterContainer && !this.$refs.filterContainer.contains(event.target)) {
        // Check if the click is on any element related to the filter modal or its dropdowns
        const isFilterRelated = event.target.closest('[data-filter-modal], .filter-popup, .vendor-dropdown, .absolute[class*="z-10"]');

        if (!isFilterRelated) {
          this.showFilterModal = false;
        }
      }
    },

    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },

    onClickPage(page) {
      this.currentPage = page;
      this.actionFetchAll();
    },
    // Utils
    // formatCount(value) {
    //   let val = (value / 1).toFixed(0).replace('.', ',');
    //   return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    // },
    // formatDate(tgl) {
    //   return new Date(tgl).toString();
    // },
    formatDate(dateString) {
      return moment(dateString).format('DD MMM YYYY')
    },
    formatTime(dateString) {
      return moment(dateString).format('HH:mm:ss')
    },

    handleTableSort(key) {
      const newActiveSort = {...this.activeSort};
      // Map table display keys to API field names
      const sortKeyMap = {
        'created_date': 'createdAt',
        'last_modified': 'updatedAt'
      };
      const apiKey = sortKeyMap[key] || key;

      if (!this.activeSort.key) {
        newActiveSort.key = apiKey;
      } else if (this.activeSort.key === apiKey) {
        newActiveSort.order = newActiveSort.order === 'desc' ? 'asc' : 'desc'
      } else if (this.activeSort.key !== apiKey) {
        newActiveSort.key = apiKey;
        newActiveSort.order = 'asc'
      }
      this.activeSort = newActiveSort;
      this.currentPage = 1; // Reset to first page when sorting
      this.actionFetchAll();
    },

  }
}
</script>
